<template>
    <div class="add-card-form">
        <p class="title">Add New Card</p>
        <div class="card-form-wrapper">
            <div class="first-row">
                <div class="card-holder-input-wrapper">
                    <label class="card-holder-label"> Card Holder * </label>
                    <input
                        v-model="$v.cardFormValues.cardholder.$model"
                        v-autofocus
                        @keydown="$v.cardFormValues.cardholder.$touch"
                        class="card-holder-input"
                        type="text"
                        placeholder="Enter the card holder's full name"
                    />
                    <span
                        v-if="
                            $v.cardFormValues.cardholder.$dirty &&
                            $v.cardFormValues.cardholder.$error
                        "
                        class="error-form-message"
                    >
                        Please enter a valid card holder name.
                    </span>
                </div>
                <div class="expire-date-input-wrapper">
                    <label class="expire-date-label"> Expiry Date * </label>
                    <input
                        v-model="$v.cardFormValues.exp_date.$model"
                        v-mask="'##/##'"
                        @keydown="$v.cardFormValues.exp_date.$touch"
                        class="exp-date-input"
                        type="text"
                        placeholder="MM/YY"
                    />
                    <span
                        v-if="
                            $v.cardFormValues.exp_date.$dirty &&
                            $v.cardFormValues.exp_date.$error
                        "
                        class="error-form-message"
                    >
                        Invalid expire.
                    </span>
                </div>
            </div>
            <div class="second-row">
                <div class="card-number-input-wrapper">
                    <label class="card-number-label"> Card Number * </label>
                    <input
                        v-model="$v.cardFormValues.card_number.$model"
                        @keydown="$v.cardFormValues.card_number.$touch"
                        @keyup="formatCreditCardInput($event)"
                        class="card-number-input"
                        type="text"
                        placeholder="Enter your card number"
                    />
                    <span
                        v-if="
                            $v.cardFormValues.card_number.$dirty &&
                            $v.cardFormValues.card_number.$error
                        "
                        class="error-form-message first-col-error"
                    >
                        Please enter a valid card number.
                    </span>
                </div>
                <div class="cvc-input-wrapper">
                    <label class="cvc-number-label"> CVC or CVV * </label>
                    <input
                        v-model="$v.cardFormValues.cvc.$model"
                        @keydown="$v.cardFormValues.cvc.$touch"
                        class="card-number-input"
                        type="text"
                        placeholder="***"
                    />
                    <span
                        v-if="$v.cardFormValues.cvc.$dirty && $v.cardFormValues.cvc.$error"
                        class="error-form-message second-col-error"
                    >
                        Invalid CVC.
                    </span>
                </div>
            </div>
            <div class="add-card-actions">
                <button @click="() => (showAddCardForm = false)" class="cancel-card-btn">
                    Cancel
                </button>
                <button
                    :style="saveCardBtnStyle"
                    :disabled="$v.cardFormValues.$invalid || duplicateCard"
                    @click="addPaymentMethod($v)"
                    class="save-card-btn"
                >
                    Save
                </button>
            </div>
        </div>
    </div>
</template>
<script>
    import { required, minLength, maxLength } from 'vuelidate/lib/validators'
    import { usePaymentMethods } from '@/composables/features/payments-methods/usePayments'

    export default {
        name: 'AddCardForm',
        setup() {
            const {
                showAddCardForm,
                cardFormValues,
                duplicateCard,
                addPaymentMethod,
                formatCreditCardInput
            } = usePaymentMethods()

            return {
                showAddCardForm,
                cardFormValues,
                duplicateCard,
                addPaymentMethod,
                formatCreditCardInput
            }
        },
        validations: {
            cardFormValues: {
                cardholder: {
                    required,
                    correctFormat: function (value) {
                        return /[a-zA-Z]+ [a-zA-Z]+/.test(value)
                    }
                },
                exp_date: {
                    required,
                    correctDateAndYear: function (value) {
                        if (value.split('/').length !== 2) {
                            return false
                        } else if (value.split('/').length === 2) {
                            const expires = value.split('/')
                            const month = expires[0]
                            const year = expires[1]
                            const currentYear = new Date().getFullYear()

                            return (
                                parseInt(month) <= 12 && parseInt(`20${year}`) >= currentYear
                            )
                        } else return false
                    }
                },
                card_number: {
                    required,
                    correctFormat: function (value) {
                        return /[0-9 ]+/.test(value)
                    }
                },
                cvc: {
                    required,
                    minLength: minLength(3),
                    maxLength: maxLength(4),
                    correctFormat: function (value) {
                        if (
                            this.cardFormValues.card_number[0] === '3' &&
                            this.cardFormValues.card_number.length === 17
                        ) {
                            return /^[0-9]{4}$/.test(value)
                        }
                        return /^[0-9]{3}$/.test(value)
                    }
                }
            }
        },
        computed: {
            saveCardBtnStyle: (self) =>
                self.$v.cardFormValues.$invalid || self.duplicateCard
                    ? {}
                    : { color: 'black', background: '#cdeb09' }
        }
    }
</script>
<style lang="scss" scoped>
    @import '../../../sass/_variables.scss';
    $mobileMax: 600px;

    @media screen and (max-width: $mobileMax) {
        .card-holder-input-wrapper,
        .card-number-input-wrapper {
            min-width: 0;
        }
    }
    p {
        margin-bottom: 0px;
    }
    button {
        border: none;
    }
    .error-form-message {
        margin-top: 5px;
        color: #ed3c0d;
        font-family: Instrument Sans;
        font-size: 10px;
        font-style: normal;
        font-weight: 600;
        line-height: 130%;
        letter-spacing: 0.14px;
    }
    .add-card-form {
        display: flex;
        flex-direction: column;
        width: 100%;
        justify-content: center;
        align-items: center;
        gap: 16px;

        .title {
            color: #f5f5f5;
            font-family: 'Instrument Sans';
            font-size: 26px;
            font-style: normal;
            font-weight: 500;
            line-height: 120%;
            letter-spacing: -0.52px;
        }
        .card-form-wrapper {
            display: flex;
            flex-direction: column;
            width: 100%;
            justify-content: center;
            align-items: center;
            gap: 16px;

            .first-row,
            .second-row {
                display: flex;
                flex-direction: row;
                width: 100%;
                justify-content: space-between;
                align-items: flex-start;
                gap: 16px;

                label {
                    color: #8b8b8b;
                    font-family: 'Instrument Sans';
                    font-size: 12px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 135%;
                    letter-spacing: 0.12px;
                }
                input {
                    padding: 10px 12px;
                    border-radius: 8px;
                    border: 1px solid #8b8b8b;
                    background: none;
                    max-width: 100%;
                    align-self: stretch;
                    color: #dedede;
                    font-family: 'Instrument Sans';
                    font-size: 16px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 125%;
                }
                input::placeholder {
                    color: #646464;
                    font-family: 'Instrument Sans';
                    font-size: 16px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 125%; /* 20px */
                }
            }
            .first-row {
                .card-holder-input-wrapper {
                    display: flex;
                    flex-direction: column;
                    width: 100%;
                    justify-content: center;
                    align-items: flex-start;
                }
                .expire-date-input-wrapper {
                    display: flex;
                    flex-direction: column;
                    width: 82px;
                    justify-content: center;
                    align-items: flex-start;
                }
            }
            .second-row {
                .card-number-input-wrapper {
                    display: flex;
                    flex-direction: column;
                    width: 100%;
                    justify-content: center;
                    align-items: flex-start;
                }
                .cvc-input-wrapper {
                    display: flex;
                    flex-direction: column;
                    width: 82px;
                    justify-content: center;
                    align-items: flex-start;
                }
            }
            .add-card-actions {
                display: flex;
                flex-direction: row;
                justify-content: center;
                align-items: center;
                gap: 24px;

                .cancel-card-btn {
                    background: none;
                    color: #acacac;
                    font-family: 'Instrument Sans';
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 125%;
                    text-decoration-line: underline;
                }
                .save-card-btn {
                    display: flex;
                    padding: 8px 16px;
                    justify-content: center;
                    align-items: center;
                    border-radius: 120px;
                    background: #464646;
                    color: #262626;
                    font-family: 'Instrument Sans';
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 115%; /* 16.1px */
                    letter-spacing: 0.14px;
                }
            }
        }
    }
</style>
